import { useMutation } from 'react-query';
import { CountryCode, SupportedCountryCodes } from '../models/country';
import { useEffect } from 'react';
import { useCountryOptions } from '~/shared/utils/form';
import create from 'zustand';
import { useBasketPaths } from '~/features/basket';
import { getRequest } from '~/shared/utils';

export type CityFromZip = {
    city: string;
    iso: string;
    zip_code: string;
};

export type CityFromZipResponse = CityFromZip[];


export async function getCityFromZip(apiUrl: string) {
    const request = getRequest<CityFromZipResponse>(apiUrl);
    return request();
}


type CityFromZipState = {
    hasPostalLookupSupport: boolean;
    setHasPostalLookupSupport: (hasSupport: boolean) => void;
};

const useBringState = create<CityFromZipState>((set) => ({
    hasPostalLookupSupport: false,
    setHasPostalLookupSupport: (hasPostalLookupSupport) => {
        set(() => ({
            hasPostalLookupSupport,
        }));
    },
}));

export const useCityByZipCode = () => {
    const { currentDeliveryKey } = useCountryOptions();
    const { hasPostalLookupSupport, setHasPostalLookupSupport } = useBringState();
    const { getCityByZipCodeUrl } = useBasketPaths();

    const countryCode = currentDeliveryKey as CountryCode;

    useEffect(() => {
        setHasPostalLookupSupport(Boolean(countryCode && SupportedCountryCodes[countryCode]));
    }, [setHasPostalLookupSupport, countryCode]);



    const { isLoading, isSuccess, mutateAsync } = useMutation<
        CityFromZipResponse,
        Error,
        string
    >(
        (zipCode: string) => getCityFromZip(`${getCityByZipCodeUrl}/${zipCode}`),
        {
            onError: () => {
                setHasPostalLookupSupport(false);
            },
            onSuccess: (data) => {
                const firstEntry = data[0];
                if (!firstEntry) {
                    setHasPostalLookupSupport(false);
                    return;
                }
                const valid = Boolean(firstEntry && firstEntry.city);
                const isStillValid = hasPostalLookupSupport && valid;
                setHasPostalLookupSupport(isStillValid);
            },
        }
    );

    // Wrap mutateAsync to return { valid, firstEntry }
    const add = async (zipCode: string) => {
        const data = await mutateAsync(zipCode);
        const firstEntry = data[0];
        const valid = Boolean(firstEntry && firstEntry.city);
        return { valid, firstEntry };
    };

    return {
        isLoading,
        add,
        isSuccess,
        hasPostalLookupSupport,
    };
};
